import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";
import { publicApi } from "@/lib/publicApi";
import { useSiteSettings } from "@/lib/SiteSettingsContext";

const WHAT_OPTIONS = ["Ordering", "Booking", "New website", "Get found", "Not sure yet"];

const INTENT_CHIPS = {
    audit:    "You're requesting: free website audit",
    ordering: "You're requesting: online ordering setup",
    booking:  "You're requesting: online booking setup",
};

const INTENT_TO_WHAT = {
    audit: "Not sure yet",
    ordering: "Ordering",
    booking: "Booking",
};

const SLUG_TO_WHAT = {
    "online-ordering-system": "Ordering",
    "whatsapp-integration": "Ordering",
    "online-booking-system": "Booking",
    "website-development": "New website",
    "seo-digital-marketing": "Get found",
    "app-development": "Not sure yet",
    "ui-ux-design": "Not sure yet",
    "marketing-designs": "Not sure yet",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()\-\s\d]+$/;

function isEmail(v) { return EMAIL_RE.test((v || "").trim()); }
function isPhone(v) {
    const s = (v || "").trim();
    if (!PHONE_RE.test(s)) return false;
    return s.replace(/\D/g, "").length >= 7;
}
function contactIsValid(v) { return isEmail(v) || isPhone(v); }

const EMPTY = { full_name: "", business_name: "", town_city: "", contact_value: "", what_do_you_need: "" };
const PLACEHOLDER_EMAILS = new Set(["hello@omegait.co.uk"]);

export default function ContactForm() {
    const [params] = useSearchParams();
    const intent = params.get("intent") || "";
    const serviceSlug = params.get("service") || "";
    const { settings } = useSiteSettings();
    const supportEmail = (() => {
        const s = (settings.contact_email || "").trim();
        return (s && !PLACEHOLDER_EMAILS.has(s)) ? s : "hello@omegait.co.uk";
    })();

    const initialWhat = useMemo(() => {
        if (INTENT_TO_WHAT[intent]) return INTENT_TO_WHAT[intent];
        if (SLUG_TO_WHAT[serviceSlug]) return SLUG_TO_WHAT[serviceSlug];
        return "";
    }, [intent, serviceSlug]);

    const [data, setData] = useState(() => ({ ...EMPTY, what_do_you_need: initialWhat }));
    const [touched, setTouched] = useState({});
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(null);
    const [serverError, setServerError] = useState("");

    useEffect(() => { if (initialWhat) setData((p) => ({ ...p, what_do_you_need: initialWhat })); }, [initialWhat]);

    const validate = (d) => {
        const e = {};
        ["full_name", "business_name", "town_city", "contact_value", "what_do_you_need"].forEach((k) => {
            if (!(d[k] || "").trim()) e[k] = "We need this to get back to you.";
        });
        if (d.contact_value && d.contact_value.trim() && !contactIsValid(d.contact_value) && !e.contact_value) {
            e.contact_value = "That email doesn't look right. Check for a typo?";
        }
        return e;
    };

    const setField = (name, value) => {
        setData((p) => ({ ...p, [name]: value }));
        setErrors((p) => ({ ...p, [name]: undefined }));
    };
    const onBlur = (name) => {
        setTouched((p) => ({ ...p, [name]: true }));
        setErrors(validate({ ...data }));
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (submitting) return;
        const e = validate(data);
        setErrors(e);
        setTouched({ full_name: true, business_name: true, town_city: true, contact_value: true, what_do_you_need: true });
        if (Object.keys(e).length) return;
        setSubmitting(true);
        setServerError("");
        try {
            const payload = { ...data };
            if (intent) payload.intent = intent;
            await publicApi.post("/enquiries", payload);
            setSubmitted({ name: data.full_name, contact: data.contact_value });
        } catch (err) {
            const status = err?.response?.status;
            const detail = err?.response?.data?.detail;
            if (status === 422 && detail) setServerError(String(detail));
            else setServerError(`Something went wrong sending that. Try again, or email us directly at ${supportEmail}.`);
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        const phoneOnly = isPhone(submitted.contact) && !isEmail(submitted.contact);
        return (
            <div
                className="border rounded-2xl p-10 text-center"
                style={{ background: "linear-gradient(180deg, var(--surface), var(--ink-2))", borderColor: "var(--line-soft)" }}
                data-testid="contact-form-success"
            >
                <div className="w-14 h-14 rounded-full grid place-items-center mx-auto" style={{ background: "rgba(45,226,198,0.14)" }}>
                    <CheckCircle2 className="w-7 h-7" style={{ color: "var(--teal)" }} />
                </div>
                <p className="font-display mt-6" style={{ fontSize: 22, fontWeight: 600, color: "var(--text)", lineHeight: 1.4 }}>
                    Thanks, {submitted.name} — we've got it. We'll be in touch within one working day.
                    {phoneOnly ? "" : " In the meantime, check your inbox for a confirmation."}
                </p>
                <Link
                    to="/"
                    style={{ color: "var(--violet-bright)", fontSize: 14, fontWeight: 500, marginTop: 22, display: "inline-flex", alignItems: "center", gap: 4 }}
                    data-testid="contact-success-home-link"
                >
                    Back to home <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    const chipText = INTENT_CHIPS[intent];
    const showErr = (name) => touched[name] && errors[name];

    return (
        <form
            onSubmit={submit}
            className="border rounded-2xl p-7 sm:p-9 space-y-5"
            style={{ background: "linear-gradient(180deg, var(--surface), var(--ink-2))", borderColor: "var(--line-soft)" }}
            data-testid="contact-form"
            noValidate
        >
            {serverError && (
                <div
                    role="alert"
                    data-testid="contact-server-error"
                    style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.3)", color: "#fecaca", padding: "10px 14px", borderRadius: 10, fontSize: 13.5 }}
                >
                    {serverError}
                </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Your name" name="full_name" value={data.full_name} onChange={setField} onBlur={onBlur} error={showErr("full_name") && errors.full_name} />
                <Field label="Business name" name="business_name" value={data.business_name} onChange={setField} onBlur={onBlur} error={showErr("business_name") && errors.business_name} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Town/city" name="town_city" value={data.town_city} onChange={setField} onBlur={onBlur} error={showErr("town_city") && errors.town_city} />
                <Field label="Email or phone" name="contact_value" value={data.contact_value} onChange={setField} onBlur={onBlur} error={showErr("contact_value") && errors.contact_value} />
            </div>

            {chipText && (
                <div
                    data-testid="contact-intent-chip"
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        background: "rgba(124,107,255,0.12)", color: "var(--violet-bright)",
                        border: "1px solid var(--line)", padding: "6px 12px",
                        borderRadius: 999, fontSize: 12.5, fontWeight: 500,
                    }}
                >
                    {chipText}
                </div>
            )}

            <div className="space-y-2">
                <label style={{ fontSize: 13.5, color: "var(--text)", fontWeight: 500 }}>What do you need?</label>
                <div className="grid sm:grid-cols-2 gap-2" role="radiogroup" aria-label="What do you need?" data-testid="contact-what-radiogroup">
                    {WHAT_OPTIONS.map((opt) => {
                        const active = data.what_do_you_need === opt;
                        const testId = `contact-what-${opt.toLowerCase().replace(/\s+/g, "-")}`;
                        return (
                            <button
                                type="button"
                                key={opt}
                                onClick={() => { setField("what_do_you_need", opt); setTouched((p) => ({ ...p, what_do_you_need: true })); }}
                                role="radio"
                                aria-checked={active}
                                data-testid={testId}
                                style={{
                                    padding: "10px 14px",
                                    borderRadius: 10,
                                    border: `1px solid ${active ? "var(--teal)" : "var(--line-soft)"}`,
                                    background: active ? "rgba(45,226,198,0.10)" : "var(--surface-2)",
                                    color: active ? "var(--teal)" : "var(--text)",
                                    fontSize: 13.5,
                                    fontWeight: active ? 600 : 500,
                                    textAlign: "left",
                                    cursor: "pointer",
                                    transition: "all 0.15s",
                                }}
                            >
                                {opt}
                            </button>
                        );
                    })}
                </div>
                {showErr("what_do_you_need") && (
                    <p role="alert" style={{ fontSize: 12.5, color: "#fca5a5" }} data-testid="contact-error-what_do_you_need">
                        {errors.what_do_you_need}
                    </p>
                )}
            </div>

            <div>
                <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary"
                    data-testid="contact-submit-button"
                    style={{ padding: "12px 20px", fontSize: 14.5, opacity: submitting ? 0.75 : 1 }}
                >
                    {submitting ? (<><Loader2 className="w-4 h-4 animate-spin" />Sending your enquiry…</>) : (<>Send my enquiry</>)}
                </button>
                <p style={{ fontSize: 12.5, color: "var(--muted-2)", marginTop: 10 }}>
                    We'll only use your details to reply. No spam, ever.
                </p>
            </div>
        </form>
    );
}

function Field({ label, name, value, onChange, onBlur, error }) {
    return (
        <div className="space-y-2">
            <label htmlFor={name} style={{ fontSize: 13.5, color: "var(--text)", fontWeight: 500 }}>{label}</label>
            <input
                id={name}
                name={name}
                type="text"
                value={value}
                onChange={(e) => onChange(name, e.target.value)}
                onBlur={() => onBlur(name)}
                data-testid={`contact-field-${name}`}
                aria-invalid={!!error}
                style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: `1px solid ${error ? "#f87171" : "var(--line-soft)"}`,
                    background: "var(--surface-2)",
                    color: "var(--text)",
                    fontSize: 14.5,
                    outline: "none",
                }}
            />
            {error && (
                <p role="alert" style={{ fontSize: 12.5, color: "#fca5a5" }} data-testid={`contact-error-${name}`}>{error}</p>
            )}
        </div>
    );
}
